import { buildService, api } from '@rd-web-markets/shared/dist/services/service';
import serviceMethods from '@rd-web-markets/shared/dist/services/service_methods';
import { useCallback } from 'react';

const getBasePath = (templateId) => `/checklist_templates/${templateId}/checklist_rows`;

const checklistRowsService = {
  useFetchAllRows: (setLoading, templateId) => {
    const [rows, setRows, fetchRows] = serviceMethods.useFetch({
      callback: useCallback(async () => {
        if (!templateId) return [];
        const service = buildService(getBasePath(templateId), { only: ['all'] });
        const response = await service.all();
        if (Array.isArray(response)) return response;
        if (response && typeof response === 'object') {
          const key = Object.keys(response).find(k => Array.isArray(response[k]));
          return key ? response[key] : [];
        }
        return [];
      }, [templateId]),
      setLoading,
      initialStateValue: []
    });

    return [rows, setRows, fetchRows];
  },

  useCreateRow: (setLoading, setRows, rows, templateId) => {
    const createRow = serviceMethods.useHttpCallback({
      callback: useCallback(async (data) => {
        if (!templateId) return;
        const service = buildService(getBasePath(templateId), { only: ['create'] });
        const response = await service.create({ checklist_row: data });
        setRows([...rows, response]);
        return response;
      }, [rows, setRows, templateId]),
      setLoading
    });

    return [createRow];
  },

  useUpdateRow: (setLoading, setRows, rows, templateId) => {
    const updateRow = serviceMethods.useUpdate({
      callback: useCallback(async ({ id, data }) => {
        if (!templateId) return;
        const service = buildService(getBasePath(templateId), { only: ['update'] });
        const response = await service.update(id, { checklist_row: data });
        setRows(rows.map(r => r.id === id ? response : r));
        return response;
      }, [rows, setRows, templateId]),
      setLoading
    });

    return [updateRow];
  },

  useDeleteRow: (setLoading, setRows, rows, templateId) => {
    const deleteRow = serviceMethods.useDelete({
      callback: useCallback(async (rowId) => {
        if (!templateId) return;
        const service = buildService(getBasePath(templateId), { only: ['delete'] });
        await service.delete(rowId);
        setRows(rows.filter(r => r.id !== Number(rowId)));
      }, [rows, setRows, templateId]),
      setLoading
    });

    return [deleteRow];
  },

  useReorderRows: (setLoading, setRows, templateId) => {
    const reorderRows = serviceMethods.useHttpCallback({
      callback: useCallback(async (orderedRows) => {
        if (!templateId) return;
        const payload = {
          order: orderedRows.map((row, idx) => ({ id: row.id, order_position: idx + 1 }))
        };
        await api.update(`${getBasePath(templateId)}/reorder`, payload);
        setRows([...orderedRows]);
      }, [setRows, templateId]),
      setLoading
    });

    return [reorderRows];
  }
};

checklistRowsService.useCrud = (setLoading, templateId) => {
  const [rows, setRows, fetchRows] = checklistRowsService.useFetchAllRows(setLoading, templateId);
  const [createRow] = checklistRowsService.useCreateRow(setLoading, setRows, rows, templateId);
  const [updateRow] = checklistRowsService.useUpdateRow(setLoading, setRows, rows, templateId);
  const [deleteRow] = checklistRowsService.useDeleteRow(setLoading, setRows, rows, templateId);
  const [reorderRows] = checklistRowsService.useReorderRows(setLoading, setRows, templateId);

  return {
    rows,
    setRows,
    fetchRows,
    createRow,
    updateRow,
    deleteRow,
    reorderRows
  };
};

export default checklistRowsService;